import React, { useRef } from 'react'
import { SendHorizonal, ImagePlus, X, Check } from 'lucide-react'
import styles from './ConversationInput.module.scss'

interface ConversationInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onKeyPress: (e: React.KeyboardEvent) => void
  disabled?: boolean
  placeholder?: string
  showImageUpload?: boolean
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  pendingImagePreview?: string | null
  readyToExecute?: boolean
  onConfirmExecution?: () => void
}

const ConversationInput: React.FC<ConversationInputProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  disabled = false,
  placeholder = 'Escribí tu mensaje...',
  showImageUpload = false,
  hasPendingImage = false,
  imageUploadAcknowledged = false,
  onImageSelect,
  onImageClear,
  pendingImagePreview = null,
  readyToExecute = false,
  onConfirmExecution
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onImageSelect) {
      onImageSelect(file)
    }
    e.target.value = ''
  }

  const canSend = value.trim() || hasPendingImage

  return (
    <div className={styles.inputWrapper}>
      <div className={styles.inputContainer}>
        {showImageUpload && (
          hasPendingImage && !imageUploadAcknowledged && pendingImagePreview ? (
            <div className={styles.pendingImagePreview}>
              <img src={pendingImagePreview} alt="Vista previa" />
              <button
                type="button"
                className={styles.clearImageBtn}
                onClick={onImageClear}
                aria-label="Quitar imagen"
              >
                <X size={12} />
              </button>
            </div>
          ) : !hasPendingImage ? (
            <label className={styles.imageUploadBtn} title="Agregar imagen" aria-label="Agregar imagen">
              <ImagePlus size={20} strokeWidth={1.8} />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={disabled}
              />
            </label>
          ) : null
        )}
        <textarea
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
        />
        {readyToExecute && onConfirmExecution ? (
          <button
            className={styles.confirmButton}
            onClick={onConfirmExecution}
            disabled={disabled}
            type="button"
            aria-label="Confirmar creación"
          >
            <Check size={18} />
            <span>Confirmar</span>
          </button>
        ) : (
          <button
            className={styles.sendButton}
            onClick={onSend}
            disabled={disabled || !canSend}
            type="button"
            aria-label="Enviar mensaje"
          >
            <SendHorizonal size={18} />
          </button>
        )}
      </div>
    </div>
  )
}

export default ConversationInput
